import { Suspense, useEffect } from "react"
import { lazyWithRetry } from "@/lib/lazyWithRetry"
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom"
import { Toaster } from "sonner"
import { useAuthStore } from "./stores/auth.store"
import { useThemeStore } from "./stores/theme.store"
import { useNotificationsStore } from "./stores/notifications.store"
import { profileService } from "./services/profile.service"
import { appWarmupService } from "./services/app-warmup.service"
import { AdminRoute, ProtectedRoute } from "./components/auth/ProtectedRoute"
import { ScrollToTop } from "./components/layout/ScrollToTop"
import { LoadingPage } from "./components/ui/loading"
import { useUpdateStore } from "./stores/update.store"
import {
  MyGamesPageSkeleton,
  MyIngressosPageSkeleton,
  MyTournamentsPageSkeleton,
  MyTeamsPageSkeleton,
  ProfilePageSkeleton,
  CalendarPageSkeleton,
  MyPanelinhasPageSkeleton,
  GameRegistrationSuccessPageSkeleton,
  GameFormPageSkeleton,
  TeamFormPageSkeleton,
  TeamDetailsPageSkeleton,
  TeamDashboardPageSkeleton,
  TeamGamesPageSkeleton,
  TeamSquadBalancePageSkeleton,
  TeamGameTodayPageSkeleton,
  TeamPublicGamesPageSkeleton,
  TeamMembersPageSkeleton,
  TeamFinancePageSkeleton,
  TeamRefundsPageSkeleton,
  GameCheckoutPageSkeleton,
  GamePaymentPixPageSkeleton,
  GameRegistrationPageSkeleton,
  GameWaitingListPageSkeleton,
  GameInterestConfirmationPageSkeleton,
  GameInterestSuccessPageSkeleton,
  GameParticipantsPageSkeleton,
  GameCancelConfirmationPageSkeleton,
  GameDetailsFullPageSkeleton,
  PaymentVerificationPageSkeleton,
  NotificationsPageSkeleton,
  MyCardsPageSkeleton,
  PanelinhaInvitePageSkeleton,
  ContentPageSkeleton,
  AdminLayoutSkeleton,
} from "./components/layout/PageSkeletons"

// Páginas críticas (LCP) - import estático
import HomePage from "./pages/HomePage"
import LoginPage from "./pages/LoginPage"
import GameDetailsPage from "./pages/GameDetailsPage"
import AuthCallbackPage from "./pages/AuthCallbackPage"

// Code splitting - lazy load para reduzir bundle inicial
const ProfilePage = lazyWithRetry(() => import("./pages/ProfilePage"))
const MyTeamsPage = lazyWithRetry(() => import("./pages/MyTeamsPage"))
const TeamFormPage = lazyWithRetry(() => import("./pages/TeamFormPage"))
const GameFormPage = lazyWithRetry(() => import("./pages/GameFormPage"))
const TeamGamesPage = lazyWithRetry(() => import("./pages/TeamGamesPage"))
const TeamGamesRankingPage = lazyWithRetry(() => import("./pages/TeamGamesRankingPage"))
const TeamSquadBalancePage = lazyWithRetry(() => import("./pages/TeamSquadBalancePage"))
const TeamPublicGamesPage = lazyWithRetry(() => import("./pages/TeamPublicGamesPage"))
const TeamGameTodayPage = lazyWithRetry(() => import("./pages/TeamGameTodayPage"))
const TeamGamesRatePage = lazyWithRetry(() => import("./pages/TeamGamesRatePage"))
const MyGamesPage = lazyWithRetry(() => import("./pages/MyGamesPage"))
const MyGamesTodayPage = lazyWithRetry(() => import("./pages/MyGamesTodayPage"))
const MyGalleryPage = lazyWithRetry(() => import("./pages/MyGalleryPage"))
const CalendarPage = lazyWithRetry(() => import("./pages/CalendarPage"))
const ScoreboardPage = lazyWithRetry(() => import("./pages/ScoreboardPage"))
const TeamDashboardPage = lazyWithRetry(() => import("./pages/TeamDashboardPage"))
const TeamDetailsPage = lazyWithRetry(() => import("./pages/TeamDetailsPage"))
const TeamMembersPage = lazyWithRetry(() => import("./pages/TeamMembersPage"))
const TeamMemberDirectoryPage = lazyWithRetry(() => import("./pages/TeamMemberDirectoryPage"))
const TeamMemberNetworkPage = lazyWithRetry(() => import("./pages/TeamMemberNetworkPage"))
const TeamMemberRankingPage = lazyWithRetry(() => import("./pages/TeamMemberRankingPage"))
const TeamQuickLinksPage = lazyWithRetry(() => import("./pages/TeamQuickLinksPage"))
const TeamFinancePage = lazyWithRetry(() => import("./pages/TeamFinancePage"))
const TeamSuppliersPage = lazyWithRetry(() => import("./pages/TeamSuppliersPage"))
const TeamSubscriptionsPage = lazyWithRetry(() => import("./pages/TeamSubscriptionsPage"))
const TeamChargePage = lazyWithRetry(() => import("./pages/TeamChargePage"))
const TeamSubscriptionPayPage = lazyWithRetry(() => import("./pages/TeamSubscriptionPayPage"))
const TeamSubscriptionSuccessPage = lazyWithRetry(() => import("./pages/TeamSubscriptionSuccessPage"))
const TeamRefundsPage = lazyWithRetry(() => import("./pages/TeamRefundsPage"))
const TeamRankingPrizePage = lazyWithRetry(() => import("./pages/TeamRankingPrizePage"))
const TeamFeedbacksPage = lazyWithRetry(() => import("./pages/TeamFeedbacksPage"))
const TeamTournamentsPage = lazyWithRetry(() => import("./pages/TeamTournamentsPage"))
const TournamentFormPage = lazyWithRetry(() => import("./pages/TournamentFormPage"))
const TournamentDetailRedirectPage = lazyWithRetry(() => import("./pages/TournamentDetailRedirectPage"))
const TournamentDetailPage = lazyWithRetry(() => import("./pages/TournamentDetailPage"))
const TournamentRegistrationPage = lazyWithRetry(() => import("./pages/TournamentRegistrationPage"))
const TournamentCheckoutPage = lazyWithRetry(() => import("./pages/TournamentCheckoutPage"))
const TournamentPaymentPixPage = lazyWithRetry(() => import("./pages/TournamentPaymentPixPage"))
const TournamentRegistrationSuccessPage = lazyWithRetry(() => import("./pages/TournamentRegistrationSuccessPage"))
const TournamentPaymentVerificationPage = lazyWithRetry(() => import("./pages/TournamentPaymentVerificationPage"))
const GameParticipantsPage = lazyWithRetry(() => import("./pages/GameParticipantsPage"))
const GameDetailsFullPage = lazyWithRetry(() => import("./pages/GameDetailsFullPage"))
const MyTournamentsPage = lazyWithRetry(() => import("./pages/MyTournamentsPage"))
const GameRegistrationPage = lazyWithRetry(() => import("./pages/GameRegistrationPage"))
const GameInterestConfirmationPage = lazyWithRetry(() => import("./pages/GameInterestConfirmationPage"))
const GameInterestSuccessPage = lazyWithRetry(() => import("./pages/GameInterestSuccessPage"))
const GameWaitingListPage = lazyWithRetry(() => import("./pages/GameWaitingListPage"))
const GameCheckoutPage = lazyWithRetry(() => import("./pages/GameCheckoutPage"))
const GamePaymentPixPage = lazyWithRetry(() => import("./pages/GamePaymentPixPage"))
const GameRegistrationSuccessPage = lazyWithRetry(() => import("./pages/GameRegistrationSuccessPage"))
const GameSummaryPage = lazyWithRetry(() => import("./pages/GameSummaryPage"))
const GameCancelConfirmationPage = lazyWithRetry(() => import("./pages/GameCancelConfirmationPage"))
const GameTeamRatingPage = lazyWithRetry(() => import("./pages/GameTeamRatingPage"))
const PaymentVerificationPage = lazyWithRetry(() => import("./pages/PaymentVerificationPage"))
const GameCheckInPage = lazyWithRetry(() => import("./pages/GameCheckInPage"))
const NotificationsPage = lazyWithRetry(() => import("./pages/NotificationsPage"))
const PwaInstallPage = lazyWithRetry(() => import("./pages/PwaInstallPage"))
const MyCardsPage = lazyWithRetry(() => import("./pages/MyCardsPage"))
const AboutPage = lazyWithRetry(() => import("./pages/AboutPage"))
const FuncionalidadesPage = lazyWithRetry(() => import("./pages/FuncionalidadesPage"))
const ComoUsarPage = lazyWithRetry(() => import("./pages/ComoUsarPage"))
const PrivacyPage = lazyWithRetry(() => import("./pages/PrivacyPage"))
const TermsPage = lazyWithRetry(() => import("./pages/TermsPage"))
const CookiesPage = lazyWithRetry(() => import("./pages/CookiesPage"))
const NotFoundPage = lazyWithRetry(() => import("./pages/NotFoundPage"))
const AdminLayout = lazyWithRetry(() => import("./components/layout/AdminLayout").then((m) => ({ default: m.AdminLayout })))
const AdminWithdrawalsPage = lazyWithRetry(() => import("./pages/AdminWithdrawalsPage"))
const AdminLabsPage = lazyWithRetry(() => import("./pages/AdminLabsPage"))
const AdminPaymentsPage = lazyWithRetry(() => import("./pages/AdminPaymentsPage"))
const AdminEarningsPage = lazyWithRetry(() => import("./pages/AdminEarningsPage"))
const AdminTeamsPage = lazyWithRetry(() => import("./pages/AdminTeamsPage"))
const AdminPlatformBalancePage = lazyWithRetry(() => import("./pages/AdminPlatformBalancePage"))
const AdminPushPage = lazyWithRetry(() => import("./pages/AdminPushPage"))
const SupportTicketsPage = lazyWithRetry(() => import("./pages/SupportTicketsPage"))
const AdminSupportPage = lazyWithRetry(() => import("./pages/AdminSupportPage"))
const MyPanelinhasPage = lazyWithRetry(() => import("./pages/MyPanelinhasPage"))
const MySubscriptionsPage = lazyWithRetry(() => import("./pages/MySubscriptionsPage"))
const PanelinhaCreatePage = lazyWithRetry(() => import("./pages/PanelinhaCreatePage"))
const PanelinhaDetailPage = lazyWithRetry(() => import("./pages/PanelinhaDetailPage"))
const PanelinhaEditPage = lazyWithRetry(() => import("./pages/PanelinhaEditPage"))
const PanelinhaInvitePage = lazyWithRetry(() => import("./pages/PanelinhaInvitePage"))
const TournamentRosterInvitePage = lazyWithRetry(() => import("./pages/TournamentRosterInvitePage"))
const GameShortLinkPage = lazyWithRetry(() => import("./pages/GameShortLinkPage"))
const TournamentShortLinkPage = lazyWithRetry(() => import("./pages/TournamentShortLinkPage"))
const TrainingShortLinkPage = lazyWithRetry(() => import("./pages/TrainingShortLinkPage"))
const TeamTrainingPage = lazyWithRetry(() => import("./pages/TeamTrainingPage"))
const TeamTrainingScheduleDetailPage = lazyWithRetry(
  () => import("./pages/TeamTrainingScheduleDetailPage"),
)
const TeamTrainingSessionsPage = lazyWithRetry(() => import("./pages/TeamTrainingSessionsPage"))
const TrainingSessionFormPage = lazyWithRetry(() => import("./pages/TrainingSessionFormPage"))
const TrainingSessionDetailPage = lazyWithRetry(() => import("./pages/TrainingSessionDetailPage"))
const TrainingSessionPublicPage = lazyWithRetry(() => import("./pages/TrainingSessionPublicPage"))
const TrainingSessionCheckoutPage = lazyWithRetry(() => import("./pages/TrainingSessionCheckoutPage"))
const TrainingSessionPaymentPixPage = lazyWithRetry(() => import("./pages/TrainingSessionPaymentPixPage"))
const TrainingSessionSuccessPage = lazyWithRetry(() => import("./pages/TrainingSessionSuccessPage"))
const MyEventsPage = lazyWithRetry(() => import("./pages/MyEventsPage"))
const MyIngressosPage = lazyWithRetry(() => import("./pages/MyIngressosPage"))
const MyClassesPage = lazyWithRetry(() => import("./pages/MyClassesPage"))
const TeamPublicTrainingPage = lazyWithRetry(() => import("./pages/TeamPublicTrainingPage"))
const TrainingClassPublicPage = lazyWithRetry(() => import("./pages/TrainingClassPublicPage"))
const TrainingClassCheckoutPage = lazyWithRetry(() => import("./pages/TrainingClassCheckoutPage"))
const TrainingClassPaymentPixPage = lazyWithRetry(() => import("./pages/TrainingClassPaymentPixPage"))
const TrainingClassSuccessPage = lazyWithRetry(() => import("./pages/TrainingClassSuccessPage"))
const TrainingEnrollmentCancelConfirmationPage = lazyWithRetry(
  () => import("./pages/TrainingEnrollmentCancelConfirmationPage"),
)

function TournamentCreateLegacyRedirect() {
  const { id } = useParams<{ id: string }>()
  const team = id?.trim() ?? ""
  if (!team) return <Navigate to="/tournaments/create" replace />
  return <Navigate to={`/tournaments/create?teamId=${encodeURIComponent(team)}`} replace />
}

function TeamEventsCreateRedirect() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const team = id?.trim() ?? ""
  if (!team) return <Navigate to="/my-teams" replace />
  return (
    <Navigate
      to={`/teams/${team}/events`}
      replace
      state={{ ...(location.state as object | null), openCreateEvent: true }}
    />
  )
}

function TeamEventsLegacyRedirect() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const team = id?.trim() ?? ""
  if (!team) return <Navigate to="/my-teams" replace />
  const suffix = location.pathname.replace(`/teams/${team}/training/sessions`, "")
  return <Navigate to={`/teams/${team}/events${suffix}${location.search}`} replace />
}

function PublicEventsLegacyRedirect() {
  const location = useLocation()
  const suffix = location.pathname.replace(/^\/training\/sessions/, "")
  return <Navigate to={`/events${suffix}${location.search}`} replace />
}

function TeamTrainingClassesLegacyRedirect() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const team = id?.trim() ?? ""
  if (!team) return <Navigate to="/my-teams" replace />
  const path = location.pathname.replace(
    `/teams/${team}/training/classes`,
    `/teams/${team}/training`,
  )
  return <Navigate to={`${path}${location.search}`} replace />
}

function AppRoutes() {
  const location = useLocation()
  const { user, loading, initialize, initialized } = useAuthStore()
  const isAuthCallback = location.pathname === '/auth/callback'
  const { initializeTheme } = useThemeStore()
  const { initializeRealtime, cleanupRealtime } = useNotificationsStore()

  useEffect(() => {
    if (!initialized) {
      initialize()
    }
  }, [initialize, initialized])

  useEffect(() => {
    const markActivity = () => {
      appWarmupService.markActivity()
    }

    const triggerWarmup = () => {
      if (!navigator.onLine) {
        return
      }
      appWarmupService.warmUpIfNeeded()
      appWarmupService.markActivity()
    }

    const handleVisible = () => {
      if (document.visibilityState === 'visible') {
        triggerWarmup()
      }
    }

    const handleFocus = () => {
      triggerWarmup()
    }

    const activityEvents: Array<keyof WindowEventMap> = ['pointerdown', 'keydown']
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, markActivity, { passive: true })
    })
    window.addEventListener('focus', handleFocus)
    document.addEventListener('visibilitychange', handleVisible)

    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, markActivity)
      })
      window.removeEventListener('focus', handleFocus)
      document.removeEventListener('visibilitychange', handleVisible)
    }
  }, [])

  // Inicializar tema quando usuário é carregado (apenas uma vez)
  // IMPORTANTE: Sempre buscar do banco, NUNCA usar localStorage para tema
  useEffect(() => {
    const { initialized: themeInitialized } = useThemeStore.getState();
    if (themeInitialized) {
      return; // Já foi inicializado, não fazer novamente
    }

    if (initialized && user) {
      // SEMPRE buscar perfil do banco para obter themePreference correto
      // NUNCA usar user.themePreference do localStorage pois pode estar desatualizado
      profileService.getProfile().then((profile) => {
        // Inicializar tema: preferência do banco primeiro, senão usa do sistema
        // profile.themePreference pode ser 'light', 'dark', ou undefined/null
        initializeTheme(profile.themePreference)

        // Atualizar usuário no store com dados do perfil (avatar, themePreference, etc.)
        // Garante que a navbar e outros componentes tenham dados atualizados do banco
        const { updateUser } = useAuthStore.getState()
        const hasChanges =
          user.themePreference !== profile.themePreference ||
          user.avatar !== profile.avatar ||
          user.name !== profile.name
        if (hasChanges) {
          updateUser({
            ...user,
            themePreference: profile.themePreference,
            avatar: profile.avatar ?? user.avatar,
            name: profile.name ?? user.name,
          })
        }
      }).catch((err) => {
        // Se falhar ao buscar perfil, usar preferência do sistema (NÃO do localStorage)
        console.warn('Erro ao buscar perfil para tema, usando sistema:', err)
        initializeTheme(undefined) // Usar preferência do sistema, não do localStorage
      })
    } else if (initialized && !user) {
      // Se não há usuário logado, usar do localStorage ou padrão 'light'
      initializeTheme(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, user]) // Removido initializeTheme das dependências para evitar loops

  // Inicializar Realtime quando usuário está logado; limpar ao deslogar
  useEffect(() => {
    if (initialized && user) {
      initializeRealtime();
    } else if (initialized && !user) {
      cleanupRealtime();
    }
  }, [initialized, user, initializeRealtime, cleanupRealtime]);

  // Só mostra loading se realmente não temos dados e ainda está carregando.
  // Nunca bloquear /auth/callback — senão o callback OAuth/Magic Link não processa.
  if (loading && !user && !initialized && !isAuthCallback) {
    return <LoadingPage message="Carregando aplicação..." />
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route
        path="/g/:shortCode/*"
        element={
          <Suspense fallback={<LoadingPage />}>
            <GameShortLinkPage />
          </Suspense>
        }
      />
      <Route
        path="/t/:shortCode/*"
        element={
          <Suspense fallback={<LoadingPage />}>
            <TournamentShortLinkPage />
          </Suspense>
        }
      />
      <Route
        path="/tr/:shortCode/*"
        element={
          <Suspense fallback={<LoadingPage />}>
            <TrainingShortLinkPage />
          </Suspense>
        }
      />
      <Route
        path="/my-games"
        element={
          <ProtectedRoute>
            <Suspense fallback={<MyGamesPageSkeleton />}>
              <MyGamesPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-games/today"
        element={
          <ProtectedRoute>
            <Suspense fallback={<MyGamesPageSkeleton />}>
              <MyGamesTodayPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-galleries"
        element={
          <ProtectedRoute>
            <Suspense fallback={<MyGamesPageSkeleton />}>
              <MyGalleryPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-ingressos"
        element={
          <ProtectedRoute>
            <Suspense fallback={<MyIngressosPageSkeleton />}>
              <MyIngressosPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-events"
        element={
          <ProtectedRoute>
            <Suspense fallback={<ContentPageSkeleton />}>
              <MyEventsPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-ct"
        element={
          <ProtectedRoute>
            <Suspense fallback={<ContentPageSkeleton />}>
              <MyClassesPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route path="/my-classes" element={<Navigate to="/my-ct" replace />} />
      <Route path="/my-training" element={<Navigate to="/my-events" replace />} />
      <Route path="/training/sessions/*" element={<PublicEventsLegacyRedirect />} />
      <Route
        path="/events/:sessionId/cancel"
        element={
          <ProtectedRoute>
            <Suspense fallback={<GameCancelConfirmationPageSkeleton />}>
              <TrainingEnrollmentCancelConfirmationPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/events/:sessionId"
        element={
          <Suspense fallback={<ContentPageSkeleton />}>
            <TrainingSessionPublicPage />
          </Suspense>
        }
      />
      <Route
        path="/events/:sessionId/checkout"
        element={
          <ProtectedRoute>
            <Suspense fallback={<GameCheckoutPageSkeleton />}>
              <TrainingSessionCheckoutPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/events/:sessionId/payment/pix"
        element={
          <ProtectedRoute>
            <Suspense fallback={<GamePaymentPixPageSkeleton />}>
              <TrainingSessionPaymentPixPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/events/:sessionId/success"
        element={
          <ProtectedRoute>
            <Suspense fallback={<GameRegistrationSuccessPageSkeleton />}>
              <TrainingSessionSuccessPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-tournaments"
        element={
          <ProtectedRoute>
            <Suspense fallback={<MyTournamentsPageSkeleton />}>
              <MyTournamentsPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <Suspense fallback={<CalendarPageSkeleton />}>
              <CalendarPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/placar"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingPage />}>
              <ScoreboardPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-teams"
        element={
          <ProtectedRoute>
            <Suspense fallback={<MyTeamsPageSkeleton />}>
              <MyTeamsPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-subscriptions"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingPage />}>
              <MySubscriptionsPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/panelinha"
        element={
          <ProtectedRoute>
            <Suspense fallback={<MyPanelinhasPageSkeleton />}>
              <MyPanelinhasPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/panelinha/create"
        element={
          <ProtectedRoute>
            <Suspense fallback={<MyPanelinhasPageSkeleton />}>
              <PanelinhaCreatePage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/panelinha/convite/:token"
        element={
          <Suspense fallback={<PanelinhaInvitePageSkeleton />}>
            <PanelinhaInvitePage />
          </Suspense>
        }
      />
      <Route
        path="/torneio/equipe/convite/:token"
        element={
          <Suspense fallback={<PanelinhaInvitePageSkeleton />}>
            <TournamentRosterInvitePage />
          </Suspense>
        }
      />
      <Route
        path="/panelinha/:id/edit"
        element={
          <ProtectedRoute>
            <Suspense fallback={<MyPanelinhasPageSkeleton />}>
              <PanelinhaEditPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/panelinha/:id"
        element={
          <ProtectedRoute>
            <Suspense fallback={<MyPanelinhasPageSkeleton />}>
              <PanelinhaDetailPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <Suspense fallback={<NotificationsPageSkeleton />}>
              <NotificationsPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-cards"
        element={
          <ProtectedRoute>
            <Suspense fallback={<MyCardsPageSkeleton />}>
              <MyCardsPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tournaments/:id/edit"
        element={
          <ProtectedRoute>
            <Suspense fallback={<GameFormPageSkeleton />}>
              <TournamentFormPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tournaments/:id"
        element={
          <Suspense fallback={<ContentPageSkeleton />}>
            <TournamentDetailRedirectPage />
          </Suspense>
        }
      />
      <Route
        path="/tournaments/:id/register"
        element={
          <Suspense fallback={<GameRegistrationPageSkeleton />}>
            <TournamentRegistrationPage />
          </Suspense>
        }
      />
      <Route
        path="/tournaments/:id/checkout"
        element={
          <ProtectedRoute>
            <Suspense fallback={<GameCheckoutPageSkeleton />}>
              <TournamentCheckoutPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tournaments/:id/payment/pix"
        element={
          <ProtectedRoute>
            <Suspense fallback={<GamePaymentPixPageSkeleton />}>
              <TournamentPaymentPixPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tournaments/:id/success"
        element={
          <ProtectedRoute>
            <Suspense fallback={<GameRegistrationSuccessPageSkeleton />}>
              <TournamentRegistrationSuccessPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/verify-tournament-payment/:code"
        element={
          <Suspense fallback={<PaymentVerificationPageSkeleton />}>
            <TournamentPaymentVerificationPage />
          </Suspense>
        }
      />
      <Route
        path="/games/create"
        element={
          <ProtectedRoute>
            <Suspense fallback={<GameFormPageSkeleton />}>
              <GameFormPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      {/* Rotas mais específicas primeiro para evitar conflito */}
      <Route
        path="/games/:id/interest/success"
        element={
          <ProtectedRoute>
            <Suspense fallback={<GameInterestSuccessPageSkeleton />}>
              <GameInterestSuccessPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/games/:id/interest"
        element={
          <ProtectedRoute>
            <Suspense fallback={<GameInterestConfirmationPageSkeleton />}>
              <GameInterestConfirmationPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/games/:id/participants"
        element={
          <Suspense fallback={<GameParticipantsPageSkeleton />}>
            <GameParticipantsPage />
          </Suspense>
        }
      />
      <Route
        path="/games/:id/register"
        element={
          <ProtectedRoute>
            <Suspense fallback={<GameRegistrationPageSkeleton />}>
              <GameRegistrationPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/games/:id/waiting"
        element={
          <ProtectedRoute>
            <Suspense fallback={<GameWaitingListPageSkeleton />}>
              <GameWaitingListPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/games/:id/waitlist"
        element={
          <ProtectedRoute>
            <Suspense fallback={<GameWaitingListPageSkeleton />}>
              <GameWaitingListPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route 
        path="/games/:id" 
        element={<GameDetailsPage />} 
      />
      <Route
        path="/games/:id/checkout"
        element={
          <ProtectedRoute>
            <Suspense fallback={<GameCheckoutPageSkeleton />}>
              <GameCheckoutPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/games/:id/payment/pix"
        element={
          <ProtectedRoute>
            <Suspense fallback={<GamePaymentPixPageSkeleton />}>
              <GamePaymentPixPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/games/:id/success"
        element={
          <ProtectedRoute>
            <Suspense fallback={<GameRegistrationSuccessPageSkeleton />}>
              <GameRegistrationSuccessPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/games/:id/summary"
        element={
          <ProtectedRoute>
            <Suspense fallback={<ContentPageSkeleton />}>
              <GameSummaryPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/games/:id/cancel"
        element={
          <ProtectedRoute>
            <Suspense fallback={<GameCancelConfirmationPageSkeleton />}>
              <GameCancelConfirmationPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/games/:id/check-in"
        element={
          <ProtectedRoute>
            <Suspense fallback={<GameParticipantsPageSkeleton />}>
              <GameCheckInPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/games/:id/rate-team"
        element={
          <ProtectedRoute>
            <Suspense fallback={<GameParticipantsPageSkeleton />}>
              <GameTeamRatingPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/verify-payment/:code"
        element={
          <Suspense fallback={<PaymentVerificationPageSkeleton />}>
            <PaymentVerificationPage />
          </Suspense>
        }
      />
      <Route
        path="/games/:id/details"
        element={
          <ProtectedRoute>
            <Suspense fallback={<GameDetailsFullPageSkeleton />}>
              <GameDetailsFullPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/games/:id/edit"
        element={
          <ProtectedRoute>
            <Suspense fallback={<GameFormPageSkeleton />}>
              <GameFormPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Suspense fallback={<ProfilePageSkeleton />}>
              <ProfilePage />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="/teams/create"
        element={
          <ProtectedRoute>
            <Suspense fallback={<TeamFormPageSkeleton />}>
              <TeamFormPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/edit"
        element={
          <ProtectedRoute>
            <Suspense fallback={<TeamFormPageSkeleton />}>
              <TeamFormPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id"
        element={
          <Suspense fallback={<TeamDetailsPageSkeleton />}>
            <TeamDetailsPage />
          </Suspense>
        }
      />
      <Route
        path="/teams/:id/dashboard"
        element={
          <ProtectedRoute>
            <Suspense fallback={<TeamDashboardPageSkeleton />}>
              <TeamDashboardPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/games/week"
        element={
          <Suspense fallback={<TeamGameTodayPageSkeleton />}>
            <TeamGameTodayPage />
          </Suspense>
        }
      />
      <Route
        path="/teams/:id/games/today"
        element={
          <Suspense fallback={<TeamGameTodayPageSkeleton />}>
            <TeamGameTodayPage />
          </Suspense>
        }
      />
      <Route
        path="/teams/:id/games/rate"
        element={
          <Suspense fallback={<TeamGameTodayPageSkeleton />}>
            <TeamGamesRatePage />
          </Suspense>
        }
      />
      <Route
        path="/teams/:id/games/monday"
        element={
          <Suspense fallback={<TeamGameTodayPageSkeleton />}>
            <TeamGameTodayPage />
          </Suspense>
        }
      />
      <Route
        path="/teams/:id/games/tuesday"
        element={
          <Suspense fallback={<TeamGameTodayPageSkeleton />}>
            <TeamGameTodayPage />
          </Suspense>
        }
      />
      <Route
        path="/teams/:id/games/wednesday"
        element={
          <Suspense fallback={<TeamGameTodayPageSkeleton />}>
            <TeamGameTodayPage />
          </Suspense>
        }
      />
      <Route
        path="/teams/:id/games/thursday"
        element={
          <Suspense fallback={<TeamGameTodayPageSkeleton />}>
            <TeamGameTodayPage />
          </Suspense>
        }
      />
      <Route
        path="/teams/:id/games/friday"
        element={
          <Suspense fallback={<TeamGameTodayPageSkeleton />}>
            <TeamGameTodayPage />
          </Suspense>
        }
      />
      <Route
        path="/teams/:id/games/saturday"
        element={
          <Suspense fallback={<TeamGameTodayPageSkeleton />}>
            <TeamGameTodayPage />
          </Suspense>
        }
      />
      <Route
        path="/teams/:id/games/sunday"
        element={
          <Suspense fallback={<TeamGameTodayPageSkeleton />}>
            <TeamGameTodayPage />
          </Suspense>
        }
      />
      <Route
        path="/teams/:id/games/public"
        element={
          <Suspense fallback={<TeamPublicGamesPageSkeleton />}>
            <TeamPublicGamesPage />
          </Suspense>
        }
      />
      <Route
        path="/teams/:id/games/ranking"
        element={
          <ProtectedRoute>
            <Suspense fallback={<TeamGamesPageSkeleton />}>
              <TeamGamesRankingPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/games"
        element={
          <ProtectedRoute>
            <Suspense fallback={<TeamGamesPageSkeleton />}>
              <TeamGamesPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/squad-balance"
        element={
          <ProtectedRoute>
            <Suspense fallback={<TeamSquadBalancePageSkeleton />}>
              <TeamSquadBalancePage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/links"
        element={
          <ProtectedRoute>
            <Suspense fallback={<TeamMembersPageSkeleton />}>
              <TeamQuickLinksPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/members/list"
        element={
          <ProtectedRoute>
            <Suspense fallback={<TeamMembersPageSkeleton />}>
              <TeamMemberDirectoryPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/members/ranking"
        element={
          <ProtectedRoute>
            <Suspense fallback={<TeamMembersPageSkeleton />}>
              <TeamMemberRankingPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/members/network"
        element={
          <ProtectedRoute>
            <Suspense fallback={<TeamMembersPageSkeleton />}>
              <TeamMemberNetworkPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/members"
        element={
          <ProtectedRoute>
            <Suspense fallback={<TeamMembersPageSkeleton />}>
              <TeamMembersPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/finance"
        element={
          <ProtectedRoute>
            <Suspense fallback={<TeamFinancePageSkeleton />}>
              <TeamFinancePage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/ranking-prize"
        element={
          <ProtectedRoute>
            <Suspense fallback={<TeamFinancePageSkeleton />}>
              <TeamRankingPrizePage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/refunds"
        element={
          <ProtectedRoute>
            <Suspense fallback={<TeamRefundsPageSkeleton />}>
              <TeamRefundsPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/feedback"
        element={
          <ProtectedRoute>
            <Suspense fallback={<TeamFinancePageSkeleton />}>
              <TeamFeedbacksPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/suppliers"
        element={
          <ProtectedRoute>
            <Suspense fallback={<TeamFinancePageSkeleton />}>
              <TeamSuppliersPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/subscriptions"
        element={
          <ProtectedRoute>
            <Suspense fallback={<TeamFinancePageSkeleton />}>
              <TeamSubscriptionsPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/charge"
        element={
          <ProtectedRoute>
            <Suspense fallback={<TeamFinancePageSkeleton />}>
              <TeamChargePage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/subscription/pay"
        element={
          <ProtectedRoute>
            <Suspense fallback={<ContentPageSkeleton />}>
              <TeamSubscriptionPayPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/subscription/success"
        element={
          <ProtectedRoute>
            <Suspense fallback={<ContentPageSkeleton />}>
              <TeamSubscriptionSuccessPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tournaments/create"
        element={
          <ProtectedRoute>
            <Suspense fallback={<GameFormPageSkeleton />}>
              <TournamentFormPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/tournaments/create"
        element={
          <ProtectedRoute>
            <TournamentCreateLegacyRedirect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/training/public"
        element={
          <Suspense fallback={<ContentPageSkeleton />}>
            <TeamPublicTrainingPage />
          </Suspense>
        }
      />
      <Route
        path="/teams/:id/training/classes/*"
        element={<TeamTrainingClassesLegacyRedirect />}
      />
      <Route
        path="/teams/:id/training/manage/:scheduleId"
        element={
          <ProtectedRoute>
            <Suspense fallback={<ContentPageSkeleton />}>
              <TeamTrainingScheduleDetailPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/training/:classId/cancel"
        element={
          <ProtectedRoute>
            <Suspense fallback={<GameCancelConfirmationPageSkeleton />}>
              <TrainingEnrollmentCancelConfirmationPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/training/:classId/checkout"
        element={
          <ProtectedRoute>
            <Suspense fallback={<GameCheckoutPageSkeleton />}>
              <TrainingClassCheckoutPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/training/:classId/payment/pix"
        element={
          <ProtectedRoute>
            <Suspense fallback={<GamePaymentPixPageSkeleton />}>
              <TrainingClassPaymentPixPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/training/:classId/success"
        element={
          <ProtectedRoute>
            <Suspense fallback={<GameRegistrationSuccessPageSkeleton />}>
              <TrainingClassSuccessPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/training/:classId"
        element={
          <Suspense fallback={<ContentPageSkeleton />}>
            <TrainingClassPublicPage />
          </Suspense>
        }
      />
      <Route
        path="/teams/:id/training"
        element={
          <ProtectedRoute>
            <Suspense fallback={<ContentPageSkeleton />}>
              <TeamTrainingPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/training/settings"
        element={<Navigate to=".." replace />}
      />
      <Route path="/teams/:id/training/sessions/*" element={<TeamEventsLegacyRedirect />} />
      <Route
        path="/teams/:id/events/create"
        element={
          <ProtectedRoute>
            <TeamEventsCreateRedirect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/events"
        element={
          <ProtectedRoute>
            <Suspense fallback={<ContentPageSkeleton />}>
              <TeamTrainingSessionsPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/events/:sessionId/edit"
        element={
          <ProtectedRoute>
            <Suspense fallback={<ContentPageSkeleton />}>
              <TrainingSessionFormPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/events/:sessionId"
        element={
          <ProtectedRoute>
            <Suspense fallback={<ContentPageSkeleton />}>
              <TrainingSessionDetailPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/tournaments"
        element={
          <ProtectedRoute>
            <Suspense fallback={<ContentPageSkeleton />}>
              <TeamTournamentsPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id/tournaments/:tournamentId"
        element={
          <ProtectedRoute>
            <Suspense fallback={<ContentPageSkeleton />}>
              <TournamentDetailPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <Suspense fallback={<ContentPageSkeleton />}>
              <SupportTicketsPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <Suspense fallback={<AdminLayoutSkeleton />}>
              <AdminLayout />
            </Suspense>
          </AdminRoute>
        }
      >
        <Route index element={<Navigate to="/admin/earnings" replace />} />
        <Route path="withdrawals" element={<AdminWithdrawalsPage />} />
        <Route path="payments" element={<AdminPaymentsPage />} />
        <Route path="support" element={<AdminSupportPage />} />
        <Route path="push" element={<AdminPushPage />} />
        <Route path="labs" element={<AdminLabsPage />} />
        <Route path="teams" element={<AdminTeamsPage />} />
        <Route path="earnings" element={<AdminEarningsPage />} />
        <Route path="balance" element={<AdminPlatformBalancePage />} />
      </Route>
      <Route
        path="/about"
        element={
          <Suspense fallback={<ContentPageSkeleton />}>
            <AboutPage />
          </Suspense>
        }
      />
      <Route
        path="/funcionalidades"
        element={
          <Suspense fallback={<ContentPageSkeleton />}>
            <FuncionalidadesPage />
          </Suspense>
        }
      />
      <Route
        path="/como-usar"
        element={
          <Suspense fallback={<ContentPageSkeleton />}>
            <ComoUsarPage />
          </Suspense>
        }
      />
      <Route
        path="/pwa-install"
        element={
          <Suspense fallback={<ContentPageSkeleton />}>
            <PwaInstallPage />
          </Suspense>
        }
      />
      <Route
        path="/privacy"
        element={
          <Suspense fallback={<ContentPageSkeleton />}>
            <PrivacyPage />
          </Suspense>
        }
      />
      <Route
        path="/terms"
        element={
          <Suspense fallback={<ContentPageSkeleton />}>
            <TermsPage />
          </Suspense>
        }
      />
      <Route
        path="/cookies"
        element={
          <Suspense fallback={<ContentPageSkeleton />}>
            <CookiesPage />
          </Suspense>
        }
      />
      <Route
        path="*"
        element={
          <Suspense fallback={<ContentPageSkeleton />}>
            <NotFoundPage />
          </Suspense>
        }
      />
    </Routes>
  )
}

function ValidateUpdateBar() {
  const [searchParams] = useSearchParams()
  const setNeedRefresh = useUpdateStore((s) => s.setNeedRefresh)
  useEffect(() => {
    if (searchParams.get("showUpdateBar") === "1") {
      setNeedRefresh(() => { window.location.reload() })
    }
  }, [searchParams, setNeedRefresh])
  return null
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <ValidateUpdateBar />
      <AppRoutes />
      <Toaster
        position="top-right"
        richColors
        offset={{ top: 'calc(32px + env(safe-area-inset-top, 0px))' }}
        mobileOffset={{ top: 'calc(16px + env(safe-area-inset-top, 0px))' }}
      />
    </BrowserRouter>
  )
}

export default App
